import { useCallback, useState } from 'react';
import { Modal, Pressable, TextInput, View, ActivityIndicator } from 'react-native';
import styled from 'styled-components/native';
import { REDESIGN_COLORS } from '../../styles/colors';
import { apiService } from '../../services/apiService';
import logger from '../../utils/logger';

// Each reason maps to a short user-facing label. The internal enum value
// (left side) is what the edge function and DB check constraint expect.
const REASONS = [
  { value: 'wrong_price', label: 'Price is off' },
  { value: 'wrong_tone', label: 'Tone is off' },
  { value: 'factually_wrong', label: 'Inaccurate facts' },
  { value: 'leaked_info', label: 'Leaked private info' },
  { value: 'other', label: 'Other' },
];

const FREE_TEXT_MAX = 2000;

// --- Styled components ---

const Overlay = styled.View`
  flex: 1;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.6);
  padding: 16px;
`;

const Card = styled.View`
  width: 100%;
  max-width: 420px;
  background-color: ${REDESIGN_COLORS.cardBg};
  border-radius: 16px;
  padding: 24px;
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.borderSubtle};
`;

const Title = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 18px;
  color: ${REDESIGN_COLORS.textPrimary};
  margin-bottom: 6px;
`;

const Subtitle = styled.Text`
  font-family: 'Satoshi-Regular';
  font-size: 13px;
  line-height: 18px;
  color: ${REDESIGN_COLORS.textSecondary};
  margin-bottom: 16px;
`;

const ChipsRow = styled.View`
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`;

const Chip = styled(Pressable)`
  padding: 8px 12px;
  border-radius: 999px;
  background-color: ${({ $active }) =>
    $active ? 'rgba(5, 86, 247, 0.20)' : 'rgba(255, 255, 255, 0.06)'};
  border-width: 1px;
  border-color: ${({ $active }) =>
    $active ? REDESIGN_COLORS.ctaBlue : REDESIGN_COLORS.borderSubtle};
`;

const ChipText = styled.Text`
  font-family: 'Satoshi-Medium';
  font-size: 13px;
  color: ${({ $active }) =>
    $active ? REDESIGN_COLORS.ctaBlue : REDESIGN_COLORS.textPrimary};
`;

const NoteInput = styled(TextInput).attrs({
  placeholderTextColor: REDESIGN_COLORS.textSecondary,
  multiline: true,
})`
  min-height: 80px;
  max-height: 160px;
  background-color: rgba(255, 255, 255, 0.04);
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.borderSubtle};
  border-radius: 8px;
  padding: 10px 12px;
  font-family: 'Satoshi-Regular';
  font-size: 14px;
  color: ${REDESIGN_COLORS.textPrimary};
  margin-bottom: 16px;
`;

const ButtonRow = styled.View`
  flex-direction: row;
  gap: 8px;
`;

const ActionButton = styled(Pressable)`
  flex: 1;
  padding: 12px;
  border-radius: 8px;
  align-items: center;
  justify-content: center;
  background-color: ${({ $primary, $disabled }) => {
    if ($primary && !$disabled) return REDESIGN_COLORS.ctaBlue;
    if ($primary && $disabled) return 'rgba(5, 86, 247, 0.30)';
    return 'rgba(255, 255, 255, 0.06)';
  }};
  border-width: 1px;
  border-color: ${({ $primary }) =>
    $primary ? REDESIGN_COLORS.ctaBlue : REDESIGN_COLORS.borderSubtle};
`;

const ActionButtonText = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 14px;
  color: ${({ $primary }) =>
    $primary ? '#FFFFFF' : REDESIGN_COLORS.textPrimary};
`;

const ErrorText = styled.Text`
  font-family: 'Satoshi-Regular';
  font-size: 12px;
  color: ${REDESIGN_COLORS.scorePoor};
  margin-top: 8px;
  text-align: center;
`;

const SuccessText = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 14px;
  color: ${REDESIGN_COLORS.scoreGood};
  text-align: center;
  padding: 12px;
`;

/**
 * ReportDraftModal — internal-only report flow for AI drafts.
 *
 * The user picks a reason chip and optionally writes a note. Submit calls
 * the report-draft edge function which writes to draft_reports (locked-down
 * service-role table) and pings Telegram.
 *
 * @param {Object} props
 * @param {boolean} props.visible
 * @param {Function} props.onClose
 * @param {string} props.messageId
 */
const ReportDraftModal = ({ visible, onClose, messageId }) => {
  const [reason, setReason] = useState(null);
  const [freeText, setFreeText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const reset = useCallback(() => {
    setReason(null);
    setFreeText('');
    setSubmitting(false);
    setError(null);
    setDone(false);
  }, []);

  const handleClose = useCallback(() => {
    if (submitting) return;
    reset();
    onClose?.();
  }, [submitting, reset, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!reason || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiService.reportDraft(messageId, reason, freeText.trim() || undefined);
      setDone(true);
      // Auto-close after a moment so the user gets the confirmation but the
      // modal doesn't linger.
      setTimeout(() => {
        reset();
        onClose?.();
      }, 1400);
    } catch (err) {
      logger.error('[ReportDraftModal] submit failed', err);
      setError(err?.body?.error || err?.message || 'Could not file report. Try again.');
      setSubmitting(false);
    }
  }, [reason, submitting, messageId, freeText, reset, onClose]);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleClose}
    >
      <Pressable style={{ flex: 1 }} onPress={handleClose}>
        <Overlay>
          <Pressable onPress={(e) => e.stopPropagation()} style={{ width: '100%', alignItems: 'center' }}>
            <Card>
              {done ? (
                <SuccessText>Thanks. We'll take a look.</SuccessText>
              ) : (
                <>
                  <Title>Report this draft</Title>
                  <Subtitle>
                    What's wrong with it? This helps us tune the agent. The dealer doesn't see anything.
                  </Subtitle>

                  <ChipsRow>
                    {REASONS.map((r) => (
                      <Chip
                        key={r.value}
                        $active={reason === r.value}
                        onPress={() => setReason(r.value)}
                        disabled={submitting}
                      >
                        <ChipText $active={reason === r.value}>{r.label}</ChipText>
                      </Chip>
                    ))}
                  </ChipsRow>

                  <NoteInput
                    placeholder="Add a quick note (optional)"
                    value={freeText}
                    onChangeText={(t) => setFreeText(t.slice(0, FREE_TEXT_MAX))}
                    editable={!submitting}
                  />

                  <ButtonRow>
                    <ActionButton onPress={handleClose} disabled={submitting}>
                      <ActionButtonText>Cancel</ActionButtonText>
                    </ActionButton>
                    <ActionButton
                      $primary
                      $disabled={!reason || submitting}
                      onPress={handleSubmit}
                      disabled={!reason || submitting}
                    >
                      {submitting ? (
                        <ActivityIndicator color="#FFFFFF" size="small" />
                      ) : (
                        <ActionButtonText $primary>Send report</ActionButtonText>
                      )}
                    </ActionButton>
                  </ButtonRow>

                  {error ? <ErrorText>{error}</ErrorText> : null}
                </>
              )}
            </Card>
          </Pressable>
        </Overlay>
      </Pressable>
    </Modal>
  );
};

export default ReportDraftModal;
